import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface TokenPack {
  id: string;
  name: string;
  description: string | null;
  token_amount: string;
  usd_price: string;
  discount_percentage: string;
  is_featured: boolean;
  display_order: number;
}

export interface TokenPurchase {
  id: string;
  token_amount: string;
  usd_price: string;
  payment_status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
  tokens_distributed: boolean;
  distributed_at: string | null;
  recipient_address: string;
  blockchain_tx_hash: string | null;
  created_at: string;
  pack_name: string;
}

export interface PurchaseHistory {
  purchases: TokenPurchase[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
  };
}

interface TokenState {
  packs: TokenPack[];
  selectedPack: TokenPack | null;
  purchaseHistory: PurchaseHistory | null;
  currentPurchase: TokenPurchase | null;
  clientSecret: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: TokenState = {
  packs: [],
  selectedPack: null,
  purchaseHistory: null,
  currentPurchase: null,
  clientSecret: null,
  isLoading: false,
  error: null,
};

const tokenSlice = createSlice({
  name: 'token',
  initialState,
  reducers: {
    setTokenPacks: (state, action: PayloadAction<TokenPack[]>) => {
      state.packs = action.payload;
      state.error = null;
    },

    setSelectedPack: (state, action: PayloadAction<TokenPack | null>) => {
      state.selectedPack = action.payload;
    },

    setClientSecret: (state, action: PayloadAction<string>) => {
      state.clientSecret = action.payload;
    },

    setPurchaseHistory: (state, action: PayloadAction<PurchaseHistory>) => {
      state.purchaseHistory = action.payload;
      state.error = null;
    },

    setCurrentPurchase: (state, action: PayloadAction<TokenPurchase | null>) => {
      state.currentPurchase = action.payload;
    },

    clearClientSecret: (state) => {
      state.clientSecret = null;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setTokenPacks,
  setSelectedPack,
  setClientSecret,
  setPurchaseHistory,
  setCurrentPurchase,
  clearClientSecret,
  setLoading,
  setError,
  clearError,
} = tokenSlice.actions;

export default tokenSlice.reducer;
